import type { ReactNode } from 'react';
import { clsx } from 'clsx';

export interface PeriodPill {
  key: string;
  label: ReactNode;
  /**
   * This pill OPENS something (a month picker, a day picker), so it must stay
   * pressable while it is the active one.
   *
   * The kit's `Segmented` makes the active item inert — correct for a plain
   * switcher, where re-selecting what is already selected does nothing. But the
   * ledger's third pill is the board's "August ▾": once you are reading August,
   * the only way to reach September is to press it again. Inert, it becomes the
   * §8 dead control — an arrow that opens nothing.
   */
  opens?: boolean;
}

/**
 * The desk's brand-filled period switcher, `.segpill` from the kit stylesheet,
 * with one behaviour the kit's `Segmented` does not offer: an active pill that
 * still fires because it opens a picker.
 *
 * Local to this role rather than added to the kit (BRIEF §3) — it is one
 * screen's need, and widening a kit component for it is how a kit stops being
 * one.
 */
export function PeriodPills({
  items,
  active,
  onSelect,
  label,
}: {
  items: PeriodPill[];
  active: string;
  onSelect: (key: string) => void;
  label: string;
}) {
  return (
    <div className="segpill" role="tablist" aria-label={label}>
      {items.map((item) => {
        const on = item.key === active;
        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={on}
            className={clsx('s', on && 'on')}
            style={on && item.opens ? { cursor: 'pointer' } : undefined}
            onClick={on && !item.opens ? undefined : () => onSelect(item.key)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
